import React from "react";
import { Link } from "react-router-dom";

import { Skeleton, Text } from "@performyard/py-components";
import type { PYColumnDef } from "@performyard/py-components";
import type { WorkflowListItem } from "@performyard/service-contracts";

import { appendCurrentLocationAsFrom } from "../../../utils/workflowsBreadcrumbs";

import { type SortDirection, WorkflowsTableShell, isLoadingRow } from "../WorkflowsTableShell";
import { formatUtcDate } from "../dateFormatting";

type AllWorkflowsTableProps = {
	workflows: WorkflowListItem[];
	isLoading: boolean;
	onSortChange: (sort: { name: string; direction: SortDirection }) => void;
	activeSort: string;
	activeSortDirection: SortDirection;
	emptyStateText: string;
};

const getProgressPercent = (workflow: WorkflowListItem): number => {
	if (workflow.totalTodoCount === 0) {
		return 100;
	}
	return Math.round((workflow.completedTodoCount / workflow.totalTodoCount) * 100);
};

const AllWorkflowsTable = ({
	workflows,
	isLoading,
	onSortChange,
	activeSort,
	activeSortDirection,
	emptyStateText,
}: AllWorkflowsTableProps) => {
	const columns: PYColumnDef<WorkflowListItem>[] = [
		{
			colId: "name",
			headerName: "Workflow",
			flex: 1,
			minWidth: 200,
			sortable: true,
			cellRenderer: (params: { data: WorkflowListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				return (
					<Link
						to={appendCurrentLocationAsFrom(`/company/workflows/${row._id}`)}
						className="text-primary-5 no-underline hover:text-primary-7 hover:underline"
					>
						<Text size="paragraph-3" weight="regular">
							<span className="py-line-clamp-1">{row.name}</span>
						</Text>
					</Link>
				);
			},
		},
		{
			colId: "subject",
			headerName: "Assignee",
			type: "employee",
			width: 220,
			valueGetter: (params: { data?: WorkflowListItem }) => params.data?.subject ?? null,
		},
		{
			colId: "start_date",
			headerName: "Start Date",
			type: "date",
			width: 140,
			sortable: true,
			valueGetter: (params: { data?: WorkflowListItem }) => params.data?.startDate ?? null,
			valueFormatter: (params: { value: unknown }) => formatUtcDate(params.value),
		},
		{
			colId: "progress",
			headerName: "Progress",
			type: "progress",
			width: 180,
			valueGetter: (params: { data?: WorkflowListItem }) => {
				const workflow = params.data;
				if (!workflow || isLoadingRow(workflow)) return null;
				return getProgressPercent(workflow);
			},
			cellRendererParams: {
				ariaLabel: "Workflow Progress",
			},
		},
		{
			colId: "overdue",
			headerName: "Overdue",
			width: 120,
			headerClass: "[&_.ag-header-cell-label]:justify-center",
			cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
			valueGetter: (params: { data?: WorkflowListItem }) => params.data?.overdueTodoCount ?? 0,
			cellRenderer: (params: { data: WorkflowListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-6" />;
				return row.overdueTodoCount;
			},
		},
	];

	return (
		<WorkflowsTableShell<WorkflowListItem>
			testId="workflows-table"
			columns={columns}
			rows={workflows}
			isLoading={isLoading}
			onSortChange={onSortChange}
			activeSort={activeSort}
			activeSortDirection={activeSortDirection}
			emptyStateText={emptyStateText}
		/>
	);
};

export default AllWorkflowsTable;
